import { useEffect, useState } from 'react';
import { api } from '../../api/api';

interface Employee {
  id: number;
  full_name: string;
  photo_url?: string;
  office_number?: string;
  description?: string;
}

export default function EmployeesList() {
  const [employees, setEmployees] = useState<Employee[]>([]);

  useEffect(() => {
    let isMounted = true;
    api.getEmployees().then(data => {
      if (isMounted && data) {
        // --- ЛОГІКА СОРТУВАННЯ: ПОСАДА + АЛФАВІТ ---
        const sortedData = [...data].sort((a, b) => {
          const getPriority = (desc: string = '') => {
            const d = desc.toLowerCase();
            if (d.includes('завідувач')) return 1;
            if (d.includes('лікар-психіатр')) return 2;
            if (d.includes('психолог')) return 3;
            return 4;
          };

          const priorityA = getPriority(a.description);
          const priorityB = getPriority(b.description);

          if (priorityA === priorityB) {
            return a.full_name.localeCompare(b.full_name, 'uk');
          }
          return priorityA - priorityB;
        });
        
        setEmployees(sortedData);
      }
    });
    return () => { isMounted = false; };
  }, []);

  const renderDescription = (text: string) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => (
      <div key={i} className="description-line">
        <span className="bullet">•</span> {line.trim()}
      </div>
    ));
  };

  return (
    <div className="employees-page-wrapper">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&display=swap');

        .employees-page-wrapper {
          --primary: #1abc9c;
          --primary-light: rgba(26, 188, 156, 0.08);
          --dark: #2d4a43;
          --bg: #f8fdfc;
          min-height: 100vh;
          background-color: var(--bg);
          padding: clamp(30px, 6vw, 80px) 16px;
          font-family: 'Montserrat', sans-serif;
          box-sizing: border-box;
        }

        .content-container { max-width: 1200px; margin: 0 auto; }
        .page-header { text-align: center; margin-bottom: 50px; }
        .main-title { font-size: clamp(26px, 5vw, 40px); color: var(--dark); font-weight: 800; margin-bottom: 12px; }
        .title-underline { width: 60px; height: 5px; background: var(--primary); margin: 0 auto 20px; border-radius: 10px; }

        .employees-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 30px;
        }

        .employee-card {
          background: #fff;
          border-radius: 32px;
          overflow: hidden;
          border: 2px solid var(--primary);
          display: flex;
          flex-direction: column;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .employee-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(26, 188, 156, 0.15);
        }

        .card-image-wrapper { 
          position: relative; 
          aspect-ratio: 1 / 1; 
          background: var(--primary-light); 
          overflow: hidden;
        }

        /* --- НАЛАШТУВАННЯ ЦЕНТРУВАННЯ ФОТО --- */
        .employee-photo { 
          width: 100%; 
          height: 100%; 
          object-fit: cover; 
          /* 50% по горизонталі, 15% по вертикалі — фокусуємося на обличчі, 
             що дає трохи більше зрізу зверху для динаміки */
          object-position: 50% 10%; 
          transition: transform 0.6s ease;
        }

        .employee-card:hover .employee-photo {
          transform: scale(1.06);
        }
        
        .office-badge { 
          position: absolute; 
          top: 18px; 
          right: 18px; 
          background: var(--primary); 
          color: #fff; 
          padding: 8px 16px; 
          border-radius: 14px; 
          font-size: 13px; 
          font-weight: 700;
          box-shadow: 0 4px 12px rgba(26, 188, 156, 0.3);
          z-index: 2;
        }

        .card-info { padding: 24px; display: flex; flex-direction: column; flex-grow: 1; }
        .card-info h3 { 
          font-size: 1.35rem; color: var(--dark); margin: 0 0 16px 0; 
          font-weight: 800; line-height: 1.2; min-height: 3.2em; 
          display: flex; align-items: center; 
        }

        .description-box {
          background: var(--primary-light);
          padding: 18px;
          border-radius: 20px;
          margin-top: auto;
          border: 1px dashed var(--primary);
        }

        .description-line {
          font-size: 14px; line-height: 1.5; color: #3e524e;
          margin-bottom: 6px; display: flex; align-items: flex-start; font-weight: 500;
        }

        .bullet { color: var(--primary); margin-right: 8px; font-weight: 900; }

        @media (max-width: 480px) {
          .employees-grid { grid-template-columns: 1fr; }
          .card-info { padding: 20px; }
          .card-info h3 { min-height: auto; margin-bottom: 12px; }
        }
      `}</style>

      <div className="content-container">
        <header className="page-header">
          <h2 className="main-title">Наші спеціалісти</h2>
          <div className="title-underline"></div>
          <p style={{ color: '#666', fontWeight: 500 }}>Професійна підтримка вашого ментального здоров'я</p>
        </header>

        <div className="employees-grid">
          {employees.map((emp) => (
            <article key={emp.id} className="employee-card">
              <div className="card-image-wrapper">
                {emp.office_number && (
                  <div className="office-badge">Каб. {emp.office_number}</div>
                )}
                {emp.photo_url ? (
                  <img 
                    src={emp.photo_url} 
                    alt={emp.full_name} 
                    className="employee-photo" 
                    loading="lazy" 
                  />
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%', 
                    color: 'var(--primary)', 
                    fontWeight: 600 
                  }}>
                    Фото оновлюється
                  </div>
                )}
              </div>

              <div className="card-info">
                <h3>{emp.full_name}</h3>
                {emp.description && (
                  <div className="description-box">
                    {renderDescription(emp.description)}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
